import React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import { cn } from "../../lib/utils";
import type { MapInteractivityModalShellProps } from "../../contracts/map-interactivity";

/**
 * MapInteractivityModalShell is a pure presentational modal shell for compact interactivity editors.
 * It provides a real backdrop dialog presentation over the full workbench, centering a large 
 * content surface suitable for popup, action, and rule editor bodies.
 *
 * This component is host-controlled via the 'open' and 'onOpenChange' props.
 */
export function MapInteractivityModalShell({
  open,
  title,
  description,
  onOpenChange,
  children,
  className,
  contentClassName,
}: MapInteractivityModalShellProps) {
  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        {/* Semi-transparent backdrop overlay with light blur */}
        <DialogPrimitive.Overlay
          className={cn(
            "fixed inset-0 z-50 bg-black/40 backdrop-blur-[2px]",
            "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
          )}
        />
        
        {/* Main dialog content surface */}
        <DialogPrimitive.Content
          className={cn(
            "fixed left-1/2 top-1/2 z-50 flex h-fit max-h-[90vh] w-[calc(100%-2rem)] max-w-4xl -translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden",
            "border border-border bg-background text-foreground shadow-2xl",
            "duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
            "data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%]",
            "data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%]",
            "rounded-radius-panel",
            className
          )}
        >
          {/* Header region with title and optional description */}
          <header className="flex flex-col space-y-1.5 border-b border-border bg-muted/20 p-6">
            <DialogPrimitive.Title className="text-xl font-semibold tracking-tight leading-tight">
              {title}
            </DialogPrimitive.Title>
            {description && (
              <DialogPrimitive.Description className="text-sm text-muted-foreground leading-relaxed">
                {description}
              </DialogPrimitive.Description>
            )}
          </header>
          
          {/* Scrollable content area for editors */}
          <div className={cn("relative flex-1 overflow-y-auto p-6", contentClassName)}>
            {children}
          </div>

          {/* Close action at the top-right corner */}
          <DialogPrimitive.Close
            className={cn(
              "absolute right-4 top-4 rounded-md p-2 transition-all opacity-60",
              "hover:bg-accent hover:text-accent-foreground hover:opacity-100",
              "focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
            )}
          >
            <X className="h-5 w-5" />
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
